import { HIRING_REQUEST_STATUS } from "../../services/hiringRequestsApi";
import { useAuth } from "../../context/AuthContext";
import { formatDateRu } from "../../utils/dateFormat";
import { getHiringRequestStatusMeta } from "../../utils/hiringRequestPresentation";

/**
 * Модальное окно просмотра заявки на подбор персонала
 */

const formatSalary = (from, to) => {
    if (!from && !to) return "Не указана";
    if (from && to) return `${from.toLocaleString()} - ${to.toLocaleString()} руб.`;
    if (from) return `от ${from.toLocaleString()} руб.`;
    return `до ${to.toLocaleString()} руб.`;
};

const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const getStatusColor = (status) => {
    switch (status) {
        case "создана":
            return "bg-blue-100 text-blue-800";
        case "завершена":
        case "закрыта":
            return "bg-green-100 text-green-800";
        case "архив":
            return "bg-slate-100 text-slate-700";
        case "возвращена на уточнение":
            return "bg-amber-100 text-amber-800";
        case "отменена":
            return "bg-red-100 text-red-800";
        default:
            return "bg-gray-100 text-gray-800";
    }
};

const InfoRow = ({ label, value }) => (
  <div className="min-w-0 rounded-xl border border-slate-100 bg-slate-50/70 px-3.5 py-3">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
    <p className="mt-1 whitespace-pre-wrap break-words text-sm font-semibold leading-5 text-slate-800">{value || "—"}</p>
  </div>
);

const ListBlock = ({ label, items }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 px-4 py-3">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <ul className="space-y-1.5 text-sm text-slate-700">
        {items.map((item, idx) => (
          <li key={idx} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#cda834]" /><span>{item}</span></li>
        ))}
      </ul>
    </div>
  );
};

const SLOT_LABELS = {
  free: "Свободно",
  planned: "Запланировано",
  adapting: "На адаптации",
  closed: "Закрыто",
};

const SLOT_STYLES = {
  free: "bg-slate-100 text-slate-700",
  planned: "bg-blue-100 text-blue-800",
  adapting: "bg-amber-100 text-amber-800",
  closed: "bg-emerald-100 text-emerald-800",
};

export default function RequestViewModal({
  request,
  onClose,
  onStatusChange,
  onEdit,
  onCreateVacancy,
  onPublishHH,
  isUpdating = false,
  isCreatingVacancy = false,
  isPublishingHH = false,
}) {
  const { user } = useAuth();
  if (!request) return null;

  const statusLabel = HIRING_REQUEST_STATUS[request.status] || request.status;
  const statusMeta = getHiringRequestStatusMeta(request.status);
  const isHr = user?.role === "hr" || user?.role === "owner" || user?.role === "dev";
  const canCreateVacancy =
    isHr &&
    !request.linked_vacancy_id &&
    !["закрыта", "завершена", "отменена", "архив", "возвращена на уточнение"].includes(request.status);
  const canPublishHH =
    isHr &&
    Boolean(request.linked_vacancy_id) &&
    !["закрыта", "завершена", "отменена", "архив", "возвращена на уточнение"].includes(request.status);

  const changeWithPrompt = (status, promptText, field) => {
    const value = window.prompt(promptText, "");
    if (value === null) return;
    if (!value.trim()) {
      window.alert("Комментарий обязателен");
      return;
    }
    onStatusChange(request.id, status, { [field]: value.trim() });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/65 p-2 backdrop-blur-sm sm:p-5">
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
        {/* Header */}
        <div className="relative shrink-0 overflow-hidden bg-slate-950 px-5 py-5 text-white sm:px-8 sm:py-6">
          <div className="absolute -right-10 -top-20 h-52 w-52 rounded-full bg-[#cda834]/20 blur-3xl" />
          <div className="relative flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-bold uppercase tracking-[0.18em] text-[#e4c45d]">{request.public_code || `Заявка №${request.id}`}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${getStatusColor(request.status)}`}>{statusLabel}</span>
              </div>
              <h2 className="mt-3 truncate text-2xl font-bold sm:text-3xl">{request.position || "Должность не указана"}</h2>
              <p className="mt-1 text-sm text-slate-300">{request.department || "Отдел не указан"}</p>
              <div className="mt-4 inline-flex max-w-full items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-sm text-slate-200">
                <span className="h-2 w-2 shrink-0 rounded-full bg-[#e4c45d]" />
                <span className="truncate"><b className="text-white">Следующий шаг:</b> {statusMeta.nextAction}</span>
              </div>
            </div>
            <button onClick={onClose} aria-label="Закрыть подробную информацию" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/10 text-2xl leading-none text-slate-300 hover:bg-white/20 hover:text-white">&times;</button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 space-y-5 overflow-y-auto bg-slate-50 p-4 sm:p-7">
          {/* Основная информация */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Основная информация</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="Должность" value={request.position} />
              <InfoRow label="Отдел" value={request.department} />
              <InfoRow label="Количество" value={request.headcount ? `${request.headcount} чел.` : null} />
              <InfoRow label="Дата выхода" value={formatDateRu(request.planned_start_date)} />
              <InfoRow label="Срочность" value={request.urgency} />
              <InfoRow label="Зарплата" value={formatSalary(request.salary_from, request.salary_to)} />
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3"><h3 className="text-sm font-bold uppercase tracking-wide text-slate-900">Места заявки</h3><span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">Всего: {request.headcount || 0}</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
              {Object.entries(SLOT_LABELS).map(([key, label]) => (
                <div key={key} className={`rounded-xl px-3 py-3 ${SLOT_STYLES[key]}`}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</div>
                  <div className="mt-1 text-2xl font-bold">{request.slot_summary?.[key] || 0}</div>
                </div>
              ))}
            </div>
            <div className="space-y-2">
              {(request.slots || []).map((slot) => (
                <div key={slot.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm">
                  <span>Место №{slot.ordinal}{slot.cycle > 1 ? ` · цикл ${slot.cycle}` : ""}</span>
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${SLOT_STYLES[slot.status] || SLOT_STYLES.free}`}>
                    {SLOT_LABELS[slot.status] || slot.status}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Контактное лицо */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Ответственные и контакты</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="ФИО руководителя" value={request.manager_name} />
              <InfoRow label="Должность руководителя" value={request.manager_position} />
              <InfoRow label="Телефон" value={request.phone} />
              <InfoRow label="Замещающий контакт" value={request.backup_contact} />
              <InfoRow label="Ответственный HR" value={request.assigned_hr_name} />
            </div>
          </div>

          {/* Основание */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Основание заявки</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="Причина открытия" value={request.reason} />
              <InfoRow label="Предыдущий сотрудник" value={request.previous_employee} />
              <InfoRow label="Испытательный срок (мес)" value={request.probation_period} />
            </div>
          </div>

          {/* Описание должности */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Описание должности</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Цель должности" value={request.purpose} />
              <InfoRow label="Особенности" value={request.features} />
              <InfoRow label="Подчинение" value={request.reporting} />
              <InfoRow label="Горизонтальные связи" value={request.horizontal_connections} />
              <InfoRow label="Перспективы роста" value={request.growth_prospects} />
            </div>
          </div>

          {/* Задачи */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Задачи</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="Ежедневные задачи" items={request.daily_tasks} />
              <ListBlock label="Еженедельные задачи" items={request.weekly_tasks} />
              <ListBlock label="Проектные задачи" items={request.project_tasks} />
            </div>
          </div>

          {/* KPI */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">KPI и ожидаемые результаты</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="KPI" items={request.kpi_metrics} />
              <ListBlock label="Результаты после ИС" items={request.expected_results_probation} />
              <ListBlock label="Приоритеты 3 месяца" items={request.priorities_3months} />
            </div>
          </div>

          {/* Требования */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Требования к кандидату</h3>
            <div className="space-y-3">
              <ListBlock label="Обязательные требования" items={request.mandatory_requirements} />
              <ListBlock label="Желаемые требования" items={request.desired_requirements} />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {request.age_from && <InfoRow label="Возраст от" value={`${request.age_from} лет`} />}
                {request.age_to && <InfoRow label="Возраст до" value={`${request.age_to} лет`} />}
                {request.gender && <InfoRow label="Пол" value={request.gender} />}
                {request.total_experience_years && <InfoRow label="Общий опыт" value={`${request.total_experience_years} лет`} />}
                {request.relevant_experience_years && <InfoRow label="Релевантный опыт" value={`${request.relevant_experience_years} лет`} />}
              </div>
            </div>
          </div>

          {/* Hard / Soft Skills */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Навыки</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="Обязательные профессиональные навыки" items={request.required_hard_skills} />
              <ListBlock label="Дополнительные профессиональные навыки" items={request.optional_hard_skills} />
              <ListBlock label="Гибкие навыки" items={request.required_soft_skills} />
              <ListBlock label="Неприемлемые качества" items={request.unacceptable_soft_skills} />
            </div>
          </div>

          {/* Компетенции и ценности */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Компетенции и ценности</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="Должностные компетенции" items={request.job_competencies} />
              <ListBlock label="Корпоративные компетенции" items={request.corporate_competencies} />
              <ListBlock label="Критичные ценности" items={request.critical_values} />
              <ListBlock label="Приемлемое поведение" items={request.acceptable_behavior} />
              <ListBlock label="Неприемлемое поведение" items={request.unacceptable_behavior} />
              <ListBlock label="Индикаторы соответствия" items={request.fit_indicators} />
              <ListBlock label="Индикаторы несоответствия" items={request.misfit_indicators} />
            </div>
          </div>

          {/* Технические требования */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Технические требования</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="Программы" items={request.software} />
              <ListBlock label="Инструменты" items={request.tools} />
              <ListBlock label="Языки" items={request.languages} />
              <InfoRow label="Внешний вид" value={request.appearance} />
            </div>
          </div>

          {/* Стратегия поиска */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Стратегия поиска</h3>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              <ListBlock label="Ключевые слова" items={request.keywords} />
              <ListBlock label="Похожие названия" items={request.similar_positions} />
              <ListBlock label="Стоп-компании" items={request.stop_companies} />
              <ListBlock label="Компании-доноры" items={request.donor_companies} />
              <ListBlock label="Источники рекомендаций" items={request.referral_sources} />
            </div>
          </div>

          {/* Условия труда */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Условия работы и доход</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="График" value={request.schedule} />
              <InfoRow label="Формат работы" value={request.work_format} />
              <InfoRow label="Адрес рабочего места" value={request.work_address} />
              <InfoRow label="Фоновый подбор" value={request.background_search ? "Включён" : "Выключен"} />
              <InfoRow label="Рабочий день" value={request.work_day_description} />
              <InfoRow label="Командировки требуются" value={request.business_trips_required ? "Да" : "Нет"} />
              <InfoRow label="Частота командировок" value={request.business_trips_frequency} />
              <InfoRow label="Направления командировок" value={request.business_trips_locations} />
              <InfoRow label="Оклад" value={formatSalary(request.salary_from, request.salary_to)} />
              <InfoRow label="Валюта" value={request.currency} />
              <InfoRow label="Оклад gross" value={request.gross ? "Да" : "Нет"} />
              <InfoRow label="Премия (тип)" value={request.bonus_type} />
              <InfoRow label="Премия (сумма/описание)" value={request.bonus_amount} />
              <InfoRow label="Условия премии" value={request.bonus_conditions} />
              <ListBlock label="Бенефиты" items={request.benefits} />
            </div>
          </div>

          {/* Тестовое */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Тестовое задание</h3>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <InfoRow label="Требуется тестовое" value={request.test_required ? "Да" : "Нет"} />
              <InfoRow label="Тест оплачивается" value={request.test_is_paid ? "Да" : "Нет"} />
              <InfoRow label="Описание теста" value={request.test_description} />
              <InfoRow label="Дедлайн теста" value={formatDateRu(request.test_deadline, request.test_deadline || "—")} />
            </div>
          </div>

          {/* Даты и статус */}
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">Связи, даты и основания решений</h3>
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
              <InfoRow label="Комментарий к уточнению" value={request.return_comment} />
              <InfoRow label="Основание закрытия" value={request.close_reason} />
              <InfoRow label="Основание отмены" value={request.cancel_reason} />
              <div>
                <p className="text-gray-500">Создана</p>
                <p>{formatDate(request.created_at)}</p>
              </div>
              <div>
                <p className="text-gray-500">Связанная вакансия</p>
                <p>
                  {request.linked_vacancy_id ? (
                    <a
                      href={`/vacancies`}
                      className="text-[#cda834] hover:underline font-medium"
                      onClick={(e) => {
                        e.preventDefault();
                        window.location.assign(`/vacancies`);
                      }}
                    >
                      #{request.linked_vacancy_id} — открыть список вакансий
                    </a>
                  ) : (
                    "—"
                  )}
                </p>
                {request.linked_vacancy_id && (
                  <p className="text-xs text-amber-700 mt-1">
                    Перед публикацией на HH укажите в вакансии профессиональную роль и город.
                  </p>
                )}
              </div>
              {request.status === "завершена" && (
                <div>
                  <p className="text-gray-500">Завершена</p>
                  <p>{formatDate(request.updated_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Workflow: vacancy + HH */}
          {(canCreateVacancy || canPublishHH || onStatusChange) && (
            <div className="rounded-2xl border border-[#e8d58b] bg-[#fffaf0] p-5 shadow-sm">
              <h3 className="mb-2 text-sm font-bold uppercase tracking-wide text-slate-900">Действия по заявке</h3>
              <p className="mb-4 text-sm text-slate-600">Доступны только корректные действия для текущего статуса.</p>
              <div className="flex flex-wrap gap-2">
              {canCreateVacancy && onCreateVacancy && (
                <button
                  type="button"
                  onClick={() => onCreateVacancy(request.id)}
                  disabled={isCreatingVacancy}
                  className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
                >
                  {isCreatingVacancy ? "Создание..." : "Создать вакансию"}
                </button>
              )}
              {canPublishHH && onPublishHH && (
                <button
                  type="button"
                  onClick={() => onPublishHH(request.id)}
                  disabled={isPublishingHH}
                  className="px-4 py-2 text-sm bg-[#e0bb48] text-black rounded-lg hover:bg-[#d4af3a] disabled:opacity-50 font-medium"
                >
                  {isPublishingHH ? "Публикация..." : "Опубликовать на HH.ru"}
                </button>
              )}
              {onStatusChange && ["создана", "на анализе"].includes(request.status) && isHr && (
                <button
                  type="button"
                  onClick={() => onStatusChange(request.id, "утверждена")}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm bg-green-100 text-green-800 rounded-lg hover:bg-green-200 disabled:opacity-50"
                >
                  {isUpdating ? "..." : "Утвердить"}
                </button>
              )}
              {onStatusChange && ["создана", "на анализе", "утверждена"].includes(request.status) && isHr && (
                <button
                  type="button"
                  onClick={() => changeWithPrompt("возвращена на уточнение", "Что необходимо уточнить руководителю?", "comment")}
                  disabled={isUpdating}
                  className="px-4 py-2 text-sm bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 disabled:opacity-50"
                >
                  Возвратить на уточнение
                </button>
              )}
              {onStatusChange && request.status === "возвращена на уточнение" && (
                <button type="button" onClick={() => onStatusChange(request.id, "на анализе")} disabled={isUpdating} className="px-4 py-2 text-sm bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 disabled:opacity-50">Отправить повторно</button>
              )}
              {onEdit && request.status === "возвращена на уточнение" && (
                <button type="button" onClick={() => onEdit(request)} className="px-4 py-2 text-sm bg-slate-900 text-white rounded-lg hover:bg-slate-800">Редактировать условия</button>
              )}
              {onStatusChange && ["утверждена", "опубликована"].includes(request.status) && isHr && (
                <button type="button" onClick={() => changeWithPrompt("закрыта", "Укажите основание закрытия", "reason")} disabled={isUpdating} className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg">Закрыть заявку</button>
              )}
              {onStatusChange && !["закрыта", "завершена", "отменена", "архив"].includes(request.status) && (
                <button type="button" onClick={() => { if (window.confirm("Перенести заявку в архив?")) onStatusChange(request.id, "архив"); }} disabled={isUpdating} className="px-4 py-2 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200">В архив</button>
              )}
              {onStatusChange && !["закрыта", "завершена", "отменена", "архив"].includes(request.status) && isHr && (
                <button type="button" onClick={() => changeWithPrompt("отменена", "Укажите основание отмены", "reason")} disabled={isUpdating} className="px-4 py-2 text-sm bg-red-50 text-red-700 rounded-lg">Отменить заявку</button>
              )}
              </div>
            </div>
          )}

          {Array.isArray(request.history) && request.history.length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-900">История заявки</h3>
              <div className="relative space-y-3 before:absolute before:bottom-3 before:left-[7px] before:top-3 before:w-px before:bg-slate-200">
                {request.history.map(item => (
                  <div key={item.id} className="relative ml-6 rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm before:absolute before:-left-[25px] before:top-5 before:h-3 before:w-3 before:rounded-full before:border-2 before:border-white before:bg-[#cda834]">
                    <div className="flex flex-col justify-between gap-1 sm:flex-row sm:gap-3"><b>{item.event_type === "conditions_updated" ? "Изменены условия" : "Изменён статус"}</b><span className="text-xs text-slate-500">{formatDate(item.created_at)}</span></div>
                    {item.from_status && <p className="text-slate-600">{item.from_status} → {item.to_status}</p>}
                    {item.comment && <p className="mt-1">{item.comment}</p>}
                    {item.changes && Object.entries(item.changes).map(([field, values]) => <p key={field} className="text-slate-600">{field}: {String(values?.from ?? "—")} → {String(values?.to ?? "—")}</p>)}
                    <p className="text-xs text-slate-400 mt-1">{item.actor_name || "Система"}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 justify-end border-t border-slate-200 bg-white px-5 py-4 sm:px-7">
          <button
            onClick={onClose}
            className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
          >
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
}
